import * as _ from 'lodash';
import { DataTransformersHelper } from '../table/data-transformers.helper';
import { MarkdownPayloadHelper } from '../content/markdown-payload.helper';
import { SNAPSHOT_TYPES } from '../../constants/chatbot-expand.constants';

const BASE_TYPES = {
  TOTAL: 'Total Base',
  ANSWER: 'Answer Base'
} as const;

/**
 * AfterSubmitHelper
 *
 * Extracted from ChatbotexpandComponent. Holds `afterSubmit`, which post-processes the
 * snapshot standard output (`standardSnapOutputData`) into per-widget/per-metric trend and
 * non-trend structures, seeds the SnapshotService page-output caches, builds the selection
 * summary title, and triggers the snapshot markdown build.
 *
 * Follows the existing `comp: any` helper pattern — the component keeps a thin wrapper
 * method with an identical signature that delegates here, so all call sites remain unchanged
 * and behavior is preserved (verbatim `this.` -> `comp.` transcription; `sortTrendTimeporiodData`
 * stays on the component and is invoked via `comp.sortTrendTimeporiodData`).
 */
export class AfterSubmitHelper {
  static afterSubmit(comp: any): void {
    comp.outputData = comp._configService.standardSnapOutputData;

    if (
      comp._configService.selectedcompareacrossOption == 'Multiple Brands' ||
      comp._configService.selectedcompareacrossOption == 'Multiple Markets'
    ) {
      comp._configService.standardSnapOutputData.sort((a: any, b: any) => {
        const sortIdA =
          comp._configService.brandSortId.find(
            (item: any) => item.name === a.colChild.split('|')[1]
          )?.sortId ?? Infinity;
        const sortIdB =
          comp._configService.brandSortId.find(
            (item: any) => item.name === b.colChild.split('|')[1]
          )?.sortId ?? Infinity;
        return sortIdA - sortIdB;
      });
    }

    comp.outputData = Array.from(
      new Set(
        comp.outputData
          .map((item: any) => {
            try {
              return JSON.stringify(item);
            } catch (err) {
              return null;
            }
          })
          .filter((item: any) => item !== null)
      )
    )
      .map((item: any) => {
        try {
          return JSON.parse(item);
        } catch (err) {
          return null;
        }
      })
      .filter((item) => item !== null);
    let arr: any[] = [];
    let arrWithoutTrend: any[] = [];

    if (!comp.widgetData || comp.widgetData.length === 0) {
      return;
    }

    comp.items = _.cloneDeep(comp.widgetData);
    if (comp._configService.selectedcompareacrossOption.includes('Multiple')) {
      if (comp.items) {
        comp.items.forEach((item: any) => {
          item.metric.forEach((itemLev1: any) => {
            let metricsAvailable = JSON.parse(itemLev1.metric_value[0] || '[]');

            if (!Array.isArray(metricsAvailable)) {
              metricsAvailable = [];
            }
            if (itemLev1.metric_group == '' && metricsAvailable.length > 0) {
              let allMetricsNull = true;

              let metrics = metricsAvailable.map(
                (metric: any) => metric.option
              );

              let result = DataTransformersHelper.checkMetrics(metrics);

              metricsAvailable.forEach((metric: any) => {
                let outputRow: any[] = [];
                comp.outputData.forEach((row: any) => {
                  if (row.rowChild == metric.option) {
                    outputRow.push(row);
                    return true;
                  }
                  return false;
                });
                if (
                  comp._configService.selectedcompareacrossOption ==
                  'Multiple Brands'
                ) {
                  outputRow.sort((a: any, b: any) => {
                    const sortIdA =
                      comp._configService.brandSortId.find(
                        (item: any) => item.name === a.colChild.split('|')[1]
                      )?.sortId ?? Infinity;
                    const sortIdB =
                      comp._configService.brandSortId.find(
                        (item: any) => item.name === b.colChild.split('|')[1]
                      )?.sortId ?? Infinity;
                    return sortIdA - sortIdB;
                  });
                }

                metric.option = DataTransformersHelper.handlemetricname(metric.option, result);
                metric.data = [];
                outputRow.forEach((row: any) => {
                  metric.data.push({
                    data: comp._configService.weightToggle(
                      comp.baseselected,
                      comp._configService.weightSelected,
                      row
                    ),
                    data2: comp._configService.weightToggleIndex(
                      comp.baseselected,
                      comp._configService.weightSelected,
                      row
                    ),
                    lvl1Child: row.Level1ColChild,
                    colChild: row.colChild,
                    rowChild: row.rowChild,
                    totalUnweightedBase: row.totalUnweightedBase,
                    totalUnweightedShare: row.totalUnweightedShare,
                    totalWeightedBase: row.totalWeightedBase,
                    totalWeightedShare: row.totalWeightedShare,
                    totalWeightedShareIndex: row.totalWeightedShareIndex,
                    answerUnweightedBase: row.answerUnweightedBase,
                    answerUnweightedShare: row.answerUnweightedShare,
                    answerWeightedBase: row.answerWeightedBase,
                    answerWeightedShare: row.answerWeightedShare,
                    answerWeightedShareIndex: row.answerWeightedShareIndex,
                    changeVsPY_Answer: row.changeVsPY_Answer,
                    changeVsPY_Total: row.changeVsPY_Total,
                    Level1ColResponseCode: row.Level1ColResponseCode,
                    Level1NestColVariableCode: row.Level1NestColVariableCode,
                    CalculationType: row.CalculationType,
                    CalculationTypeIndex: row.CalculationTypeIndex,
                    baseValueforLSorIs:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.totalUnweightedBase
                        : row.answerUnweightedBase,
                    changeVsPY:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.changeVsPY_Total
                        : row.changeVsPY_Answer,
                    Significance_Value:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.Significance_Total
                        : row.Significance_Answer,
                  });

                  if (row.totalWeightedShare !== null) {
                    allMetricsNull = false;
                  }
                });

                metric.data = comp.sortTrendTimeporiodData(metric.data);
              });
              if (allMetricsNull) {
                item.metric = [];
                item.noData = true;
                item.noDataAvailble = true;
              } else {
                item.noDataAvailble = false;
              }
            } else if (
              itemLev1.metric_group != '' &&
              metricsAvailable.length == 0
            ) {
              let allMetricsNull = true;
              const metrics = comp.outputData.filter(
                (row: any) => item.widget_name == row.widgetName
              );

              const groupedByRowChild = metrics.reduce((acc: any, obj: any) => {
                const key = obj.rowChild || 'undefined';
                if (!acc[key]) {
                  acc[key] = [];
                }
                acc[key].push(obj);
                return acc;
              }, {});

              let result = Object.keys(groupedByRowChild).map((key) => ({
                name: key,
                value: groupedByRowChild[key],
              }));
              let maxmetriclimit = JSON.parse(
                item?.customizationProperties
              )?.maxMetricLimit;
              if (maxmetriclimit != undefined) {
                result = result.slice(0, maxmetriclimit);
              }

              if (result.length > 0) {
                result.forEach((metric: any, index: number) => {
                  metricsAvailable.push({
                    is_brandanalysis: 0,
                    metricName: DataTransformersHelper.handlemetricname(metric.name, 1),
                    selectorder: index + 1,
                    data: [],
                  });

                  metric.value.forEach((row: any) => {
                    metricsAvailable[index].data.push({
                      data: comp._configService.weightToggle(
                        comp.baseselected,
                        comp._configService.weightSelected,
                        row
                      ),
                      data2: comp._configService.weightToggleIndex(
                        comp.baseselected,
                        comp._configService.weightSelected,
                        row
                      ),
                      lvl1Child: row.Level1ColChild,
                      colChild: row.colChild,
                      rowChild: row.rowChild,
                      totalUnweightedBase: row.totalUnweightedBase,
                      totalUnweightedShare: row.totalUnweightedShare,
                      totalWeightedBase: row.totalWeightedBase,
                      totalWeightedShare: row.totalWeightedShare,
                      totalWeightedShareIndex: row.totalWeightedShareIndex,
                      answerUnweightedBase: row.answerUnweightedBase,
                      answerUnweightedShare: row.answerUnweightedShare,
                      answerWeightedBase: row.answerWeightedBase,
                      answerWeightedShare: row.answerWeightedShare,
                      answerWeightedShareIndex: row.answerWeightedShareIndex,
                      changeVsPY_Answer: row.changeVsPY_Answer,
                      changeVsPY_Total: row.changeVsPY_Total,
                      Level1ColResponseCode: row.Level1ColResponseCode,
                      Level1NestColVariableCode: row.Level1NestColVariableCode,
                      CalculationType: row.CalculationType,
                      CalculationTypeIndex: row.CalculationTypeIndex,
                      baseValueforLSorIs:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.totalUnweightedBase
                          : row.answerUnweightedBase,
                      changeVsPY:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.changeVsPY_Total
                          : row.changeVsPY_Answer,
                      Significance_Value:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.Significance_Total
                          : row.Significance_Answer,
                    });

                    metricsAvailable[index].data = comp.sortTrendTimeporiodData(
                      metricsAvailable[index].data
                    );
                    if (row.totalWeightedShare !== null) {
                      allMetricsNull = false;
                    }
                  });
                });

                if (allMetricsNull) {
                  item.metric = [];
                  item.noData = true;
                  item.noDataAvailble = true;
                } else {
                  item.noDataAvailble = false;
                }
              }
            }
            itemLev1.metric_value = metricsAvailable;
          });
          if (
            item.noDataAvailble != null &&
            item.noDataAvailble != undefined &&
            item.noDataAvailble == false
          ) {
            item.widgetNotConfigured = item.metric.length > 0 ? false : true;
          } else {
            if (
              item.noDataAvailble == null ||
              item.noDataAvailble == undefined
            ) {
              item.widgetNotConfigured = item.metric.length > 0 ? false : true;
            } else {
              item.widgetNotConfigured = false;
            }
          }
        });
      }
      arr.push(comp.items);

      comp.itemWithoutTrendItems = _.cloneDeep(comp.widgetData);
      let brands: string[] = _.uniq(
        comp.outputData.map((metric: any) => metric.colChild)
      );
      if (comp.itemWithoutTrendItems) {
        comp.itemWithoutTrendItems.forEach((item: any) => {
          item.metric.forEach((itemLev1: any) => {
            let metricsAvailable = JSON.parse(itemLev1.metric_value[0] || '[]');

            if (!Array.isArray(metricsAvailable)) {
              metricsAvailable = [];
            }
            if (itemLev1.metric_group == '' && metricsAvailable.length > 0) {
              let allMetricsNull = true;
              let metrics = metricsAvailable.map(
                (metric: any) => metric.option
              );

              let result = DataTransformersHelper.checkMetrics(metrics);
              let resultBrands = DataTransformersHelper.checkBrands(brands);

              metricsAvailable.forEach((metric: any) => {
                let outputRow: any[] = [];

                for (const row of comp.outputData) {
                  if (
                    row.rowChild === metric.option &&
                    item.widget_name === row.widgetName &&
                    comp._configService.selectedTPforNonTrendView ==
                    row.Level1ColChild
                  ) {
                    outputRow.push(row);
                  }
                }

                if (
                  comp._configService.selectedcompareacrossOption ==
                  'Multiple Brands'
                ) {
                  outputRow.sort((a: any, b: any) => {
                    const sortIdA =
                      comp._configService.brandSortId.find(
                        (item: any) => item.name === a.colChild.split('|')[1]
                      )?.sortId ?? Infinity;
                    const sortIdB =
                      comp._configService.brandSortId.find(
                        (item: any) => item.name === b.colChild.split('|')[1]
                      )?.sortId ?? Infinity;
                    return sortIdA - sortIdB;
                  });
                }

                metric.option = DataTransformersHelper.handlemetricname(metric.option, result);
                metric.data = [];
                outputRow.forEach((row: any) => {
                  metric.data.push({
                    data: comp._configService.weightToggle(
                      comp.baseselected,
                      comp._configService.weightSelected,
                      row
                    ),
                    data2: comp._configService.weightToggleIndex(
                      comp.baseselected,
                      comp._configService.weightSelected,
                      row
                    ),
                    lvl1Child: row.Level1ColChild,
                    colChild: DataTransformersHelper.handleBrandName(row.colChild, resultBrands),
                    rowChild: row.rowChild,
                    totalUnweightedBase: row.totalUnweightedBase,
                    totalUnweightedShare: row.totalUnweightedShare,
                    totalWeightedBase: row.totalWeightedBase,
                    totalWeightedShare: row.totalWeightedShare,
                    totalWeightedShareIndex: row.totalWeightedShareIndex,
                    answerUnweightedBase: row.answerUnweightedBase,
                    answerUnweightedShare: row.answerUnweightedShare,
                    answerWeightedBase: row.answerWeightedBase,
                    answerWeightedShare: row.answerWeightedShare,
                    answerWeightedShareIndex: row.answerWeightedShareIndex,
                    changeVsPY_Answer: row.changeVsPY_Answer,
                    changeVsPY_Total: row.changeVsPY_Total,
                    Level1ColResponseCode: row.Level1ColResponseCode,
                    Level1NestColVariableCode: row.Level1NestColVariableCode,
                    CalculationType: row.CalculationType,
                    CalculationTypeIndex: row.CalculationTypeIndex,
                    baseValueforLSorIs:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.totalUnweightedBase
                        : row.answerUnweightedBase,
                    changeVsPY:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.changeVsPY_Total
                        : row.changeVsPY_Answer,
                    Significance_Value:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.Significance_Total
                        : row.Significance_Answer,
                  });
                  if (row.totalWeightedShare !== null) {
                    allMetricsNull = false;
                  }
                });
              });
              if (allMetricsNull) {
                item.metric = [];
                item.noData = true;
                item.noDataAvailble = true;
              } else {
                item.noDataAvailble = false;
              }
            } else if (
              itemLev1.metric_group != '' &&
              metricsAvailable.length == 0
            ) {
              let allMetricsNull = true;
              const metrics = comp.outputData.filter(
                (row: any) =>
                  item.widget_name == row.widgetName &&
                  comp._configService.selectedTPforNonTrendView ==
                  row.Level1ColChild
              );

              const groupedByRowChild = metrics.reduce((acc: any, obj: any) => {
                const key = obj.rowChild || 'undefined';
                if (!acc[key]) {
                  acc[key] = [];
                }
                acc[key].push(obj);
                return acc;
              }, {});

              const result = Object.keys(groupedByRowChild).map((key) => ({
                name: key,
                value: groupedByRowChild[key],
              }));

              if (result.slice(0, 6).length > 0) {
                result.slice(0, 6).forEach((metric: any, index: number) => {
                  metricsAvailable.push({
                    is_brandanalysis: 0,
                    option: DataTransformersHelper.handlemetricname(metric.name, 1),
                    selectorder: index + 1,
                    data: [],
                  });

                  metric.value.forEach((row: any) => {
                    metricsAvailable[index].data.push({
                      data: comp._configService.weightToggle(
                        comp.baseselected,
                        comp._configService.weightSelected,
                        row
                      ),
                      data2: comp._configService.weightToggleIndex(
                        comp.baseselected,
                        comp._configService.weightSelected,
                        row
                      ),
                      lvl1Child: row.Level1ColChild,
                      colChild: row.colChild,
                      rowChild: row.rowChild,
                      totalUnweightedBase: row.totalUnweightedBase,
                      totalUnweightedShare: row.totalUnweightedShare,
                      totalWeightedBase: row.totalWeightedBase,
                      totalWeightedShare: row.totalWeightedShare,
                      totalWeightedShareIndex: row.totalWeightedShareIndex,
                      answerUnweightedBase: row.answerUnweightedBase,
                      answerUnweightedShare: row.answerUnweightedShare,
                      answerWeightedBase: row.answerWeightedBase,
                      answerWeightedShare: row.answerWeightedShare,
                      answerWeightedShareIndex: row.answerWeightedShareIndex,
                      changeVsPY_Answer: row.changeVsPY_Answer,
                      changeVsPY_Total: row.changeVsPY_Total,
                      Level1ColResponseCode: row.Level1ColResponseCode,
                      Level1NestColVariableCode: row.Level1NestColVariableCode,
                      CalculationType: row.CalculationType,
                      CalculationTypeIndex: row.CalculationTypeIndex,
                      baseValueforLSorIs:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.totalUnweightedBase
                          : row.answerUnweightedBase,
                      changeVsPY:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.changeVsPY_Total
                          : row.changeVsPY_Answer,
                      Significance_Value:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.Significance_Total
                          : row.Significance_Answer,
                    });
                    if (row.totalWeightedShare !== null) {
                      allMetricsNull = false;
                    }
                  });
                });

                if (allMetricsNull) {
                  item.metric = [];
                  item.noData = true;
                  item.noDataAvailble = true;
                } else {
                  item.noDataAvailble = false;
                }
              }
            }

            itemLev1.metric_value = metricsAvailable;
          });
          if (
            item.noDataAvailble != null &&
            item.noDataAvailble != undefined &&
            item.noDataAvailble == false
          ) {
            item.widgetNotConfigured = item.metric.length > 0 ? false : true;
          } else {
            if (
              item.noDataAvailble == null ||
              item.noDataAvailble == undefined
            ) {
              item.widgetNotConfigured = item.metric.length > 0 ? false : true;
            } else {
              item.widgetNotConfigured = false;
            }
          }
        });
      }
      arrWithoutTrend.push(comp.itemWithoutTrendItems);
    } else {
      if (comp.items) {
        comp.items.forEach((item: any) => {
          item.metric.forEach((itemLev1: any) => {
            let metricsAvailable = JSON.parse(itemLev1.metric_value[0] || '[]');

            if (!Array.isArray(metricsAvailable)) {
              metricsAvailable = [];
            }
            if (itemLev1.metric_group == '' && metricsAvailable.length > 0) {
              let allMetricsNull = true;
              let metrics = metricsAvailable.map(
                (metric: any) => metric.option
              );

              let result = DataTransformersHelper.checkMetrics(metrics);

              metricsAvailable.forEach((metric: any) => {
                let outputRow: any[] = [];
                comp.outputData.forEach((row: any) => {
                  if (row.rowChild == metric.option) {
                    outputRow.push(row);
                    return true;
                  }
                  return false;
                });

                metric.option = DataTransformersHelper.handlemetricname(metric.option, result);
                metric.data = [];
                outputRow.forEach((row: any) => {
                  metric.data.push({
                    data: comp._configService.weightToggle(
                      comp.baseselected,
                      comp._configService.weightSelected,
                      row
                    ),
                    data2: comp._configService.weightToggleIndex(
                      comp.baseselected,
                      comp._configService.weightSelected,
                      row
                    ),
                    lvl1Child: row.Level1ColChild,
                    colChild: row.colChild,
                    rowChild: row.rowChild,
                    totalUnweightedBase: row.totalUnweightedBase,
                    totalUnweightedShare: row.totalUnweightedShare,
                    totalWeightedBase: row.totalWeightedBase,
                    totalWeightedShare: row.totalWeightedShare,
                    totalWeightedShareIndex: row.totalWeightedShareIndex,
                    answerUnweightedBase: row.answerUnweightedBase,
                    answerUnweightedShare: row.answerUnweightedShare,
                    answerWeightedBase: row.answerWeightedBase,
                    answerWeightedShare: row.answerWeightedShare,
                    answerWeightedShareIndex: row.answerWeightedShareIndex,
                    changeVsPY_Answer: row.changeVsPY_Answer,
                    changeVsPY_Total: row.changeVsPY_Total,
                    Level1ColResponseCode: row.Level1ColResponseCode,
                    Level1NestColVariableCode: row.Level1NestColVariableCode,
                    CalculationType: row.CalculationType,
                    CalculationTypeIndex: row.CalculationTypeIndex,
                    baseValueforLSorIs:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.totalUnweightedBase
                        : row.answerUnweightedBase,
                    changeVsPY:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.changeVsPY_Total
                        : row.changeVsPY_Answer,
                    Significance_Value:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.Significance_Total
                        : row.Significance_Answer,
                  });

                  if (row.totalWeightedShare !== null) {
                    allMetricsNull = false;
                  }
                });

                metric.data = comp.sortTrendTimeporiodData(metric.data);
              });
              if (allMetricsNull) {
                item.metric = [];
                item.noData = true;
                item.noDataAvailble = true;
              } else {
                item.noDataAvailble = false;
              }
            } else if (
              itemLev1.metric_group != '' &&
              metricsAvailable.length == 0
            ) {
              let allMetricsNull = true;
              const metrics = comp.outputData.filter(
                (row: any) => item.widget_name == row.widgetName
              );

              const groupedByRowChild = metrics.reduce((acc: any, obj: any) => {
                const key = obj.rowChild || 'undefined';
                if (!acc[key]) {
                  acc[key] = [];
                }
                acc[key].push(obj);
                return acc;
              }, {});

              const result = Object.keys(groupedByRowChild).map((key) => ({
                name: key,
                value: groupedByRowChild[key],
              }));

              if (result.length > 0) {
                result.forEach((metric: any, index: number) => {
                  metricsAvailable.push({
                    is_brandanalysis: 0,
                    metricName: DataTransformersHelper.handlemetricname(metric.name, 1),
                    selectorder: index + 1,
                    data: [],
                  });

                  metric.value.forEach((row: any) => {
                    metricsAvailable[index].data.push({
                      data: comp._configService.weightToggle(
                        comp.baseselected,
                        comp._configService.weightSelected,
                        row
                      ),
                      data2: comp._configService.weightToggleIndex(
                        comp.baseselected,
                        comp._configService.weightSelected,
                        row
                      ),
                      lvl1Child: row.Level1ColChild,
                      colChild: row.colChild,
                      rowChild: row.rowChild,
                      totalUnweightedBase: row.totalUnweightedBase,
                      totalUnweightedShare: row.totalUnweightedShare,
                      totalWeightedBase: row.totalWeightedBase,
                      totalWeightedShare: row.totalWeightedShare,
                      totalWeightedShareIndex: row.totalWeightedShareIndex,
                      answerUnweightedBase: row.answerUnweightedBase,
                      answerUnweightedShare: row.answerUnweightedShare,
                      answerWeightedBase: row.answerWeightedBase,
                      answerWeightedShare: row.answerWeightedShare,
                      answerWeightedShareIndex: row.answerWeightedShareIndex,
                      changeVsPY_Answer: row.changeVsPY_Answer,
                      changeVsPY_Total: row.changeVsPY_Total,
                      Level1ColResponseCode: row.Level1ColResponseCode,
                      Level1NestColVariableCode: row.Level1NestColVariableCode,
                      CalculationType: row.CalculationType,
                      CalculationTypeIndex: row.CalculationTypeIndex,
                      baseValueforLSorIs:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.totalUnweightedBase
                          : row.answerUnweightedBase,
                      changeVsPY:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.changeVsPY_Total
                          : row.changeVsPY_Answer,
                      Significance_Value:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.Significance_Total
                          : row.Significance_Answer,
                    });

                    metricsAvailable[index].data = comp.sortTrendTimeporiodData(
                      metricsAvailable[index].data
                    );
                    if (row.totalWeightedShare !== null) {
                      allMetricsNull = false;
                    }
                  });
                });

                if (allMetricsNull) {
                  item.metric = [];
                  item.noData = true;
                  item.noDataAvailble = true;
                } else {
                  item.noDataAvailble = false;
                }
              }
            }
            itemLev1.metric_value = metricsAvailable;
          });
          if (
            item.noDataAvailble != null &&
            item.noDataAvailble != undefined &&
            item.noDataAvailble == false
          ) {
            item.widgetNotConfigured = item.metric.length > 0 ? false : true;
          } else {
            if (
              item.noDataAvailble == null ||
              item.noDataAvailble == undefined
            ) {
              item.widgetNotConfigured = item.metric.length > 0 ? false : true;
            } else {
              item.widgetNotConfigured = false;
            }
          }
        });
      }
      arr.push(comp.items);

      comp.itemWithoutTrendItems = _.cloneDeep(comp.widgetData);
      if (comp.itemWithoutTrendItems) {
        comp.itemWithoutTrendItems.forEach((item: any) => {
          item.metric.forEach((itemLev1: any) => {
            let metricsAvailable = JSON.parse(itemLev1.metric_value[0] || '[]');

            if (!Array.isArray(metricsAvailable)) {
              metricsAvailable = [];
            }
            if (itemLev1.metric_group == '' && metricsAvailable.length > 0) {
              let allMetricsNull = true;
              let metrics = metricsAvailable.map(
                (metric: any) => metric.option
              );
              let result = DataTransformersHelper.checkMetrics(metrics);

              metricsAvailable.forEach((metric: any) => {
                let outputRow: any[] = [];

                for (const row of comp.outputData) {
                  if (
                    row.rowChild === metric.option &&
                    item.widget_name === row.widgetName &&
                    comp._configService.selectedTPforNonTrendView ==
                    row.Level1ColChild
                  ) {
                    outputRow.push(row);
                  }
                }

                metric.option = DataTransformersHelper.handlemetricname(metric.option, result);
                metric.data = [];
                outputRow.forEach((row: any) => {
                  metric.data.push({
                    data: comp._configService.weightToggle(
                      comp.baseselected,
                      comp._configService.weightSelected,
                      row
                    ),
                    data2: comp._configService.weightToggleIndex(
                      comp.baseselected,
                      comp._configService.weightSelected,
                      row
                    ),
                    lvl1Child: row.Level1ColChild,
                    colChild: row.colChild,
                    rowChild: row.rowChild,
                    totalUnweightedBase: row.totalUnweightedBase,
                    totalUnweightedShare: row.totalUnweightedShare,
                    totalWeightedBase: row.totalWeightedBase,
                    totalWeightedShare: row.totalWeightedShare,
                    totalWeightedShareIndex: row.totalWeightedShareIndex,
                    answerUnweightedBase: row.answerUnweightedBase,
                    answerUnweightedShare: row.answerUnweightedShare,
                    answerWeightedBase: row.answerWeightedBase,
                    answerWeightedShare: row.answerWeightedShare,
                    answerWeightedShareIndex: row.answerWeightedShareIndex,
                    changeVsPY_Answer: row.changeVsPY_Answer,
                    changeVsPY_Total: row.changeVsPY_Total,
                    Level1ColResponseCode: row.Level1ColResponseCode,
                    Level1NestColVariableCode: row.Level1NestColVariableCode,
                    CalculationType: row.CalculationType,
                    CalculationTypeIndex: row.CalculationTypeIndex,
                    baseValueforLSorIs:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.totalUnweightedBase
                        : row.answerUnweightedBase,
                    changeVsPY:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.changeVsPY_Total
                        : row.changeVsPY_Answer,
                    Significance_Value:
                      comp.baseselected.toLowerCase() == 'total base'
                        ? row.Significance_Total
                        : row.Significance_Answer,

                  });
                  if (row.totalWeightedShare !== null) {
                    allMetricsNull = false;
                  }
                });
              });
              if (allMetricsNull) {
                item.metric = [];
                item.noData = true;
                item.noDataAvailble = true;
              } else {
                item.noDataAvailble = false;
              }
            } else if (
              itemLev1.metric_group != '' &&
              metricsAvailable.length == 0
            ) {
              let allMetricsNull = true;
              const metrics = comp.outputData.filter(
                (row: any) =>
                  item.widget_name == row.widgetName &&
                  comp._configService.selectedTPforNonTrendView ==
                  row.Level1ColChild
              );

              const groupedByRowChild = metrics.reduce((acc: any, obj: any) => {
                const key = obj.rowChild || 'undefined';
                if (!acc[key]) {
                  acc[key] = [];
                }
                acc[key].push(obj);
                return acc;
              }, {});

              let result = Object.keys(groupedByRowChild).map((key) => ({
                name: key,
                value: groupedByRowChild[key],
              }));
              //;
              let maxmetriclimit = JSON.parse(
                item?.customizationProperties
              )?.maxMetricLimit;
              if (maxmetriclimit != undefined) {
                result = result.slice(0, maxmetriclimit);
              }

              if (result.length > 0) {
                result.forEach((metric: any, index: number) => {
                  metricsAvailable.push({
                    is_brandanalysis: 0,
                    option: DataTransformersHelper.handlemetricname(metric.name, 1),
                    selectorder: index + 1,
                    data: [],
                  });

                  metric.value.forEach((row: any) => {
                    metricsAvailable[index].data.push({
                      data: comp._configService.weightToggle(
                        comp.baseselected,
                        comp._configService.weightSelected,
                        row
                      ),
                      data2: comp._configService.weightToggleIndex(
                        comp.baseselected,
                        comp._configService.weightSelected,
                        row
                      ),
                      lvl1Child: row.Level1ColChild,
                      colChild: row.colChild,
                      rowChild: row.rowChild,
                      totalUnweightedBase: row.totalUnweightedBase,
                      totalUnweightedShare: row.totalUnweightedShare,
                      totalWeightedBase: row.totalWeightedBase,
                      totalWeightedShare: row.totalWeightedShare,
                      totalWeightedShareIndex: row.totalWeightedShareIndex,
                      answerUnweightedBase: row.answerUnweightedBase,
                      answerUnweightedShare: row.answerUnweightedShare,
                      answerWeightedBase: row.answerWeightedBase,
                      answerWeightedShare: row.answerWeightedShare,
                      answerWeightedShareIndex: row.answerWeightedShareIndex,
                      changeVsPY_Answer: row.changeVsPY_Answer,
                      changeVsPY_Total: row.changeVsPY_Total,
                      Level1ColResponseCode: row.Level1ColResponseCode,
                      Level1NestColVariableCode: row.Level1NestColVariableCode,
                      CalculationType: row.CalculationType,
                      CalculationTypeIndex: row.CalculationTypeIndex,
                      baseValueforLSorIs:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.totalUnweightedBase
                          : row.answerUnweightedBase,
                      changeVsPY:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.changeVsPY_Total
                          : row.changeVsPY_Answer,
                      Significance_Value:
                        comp.baseselected.toLowerCase() == 'total base'
                          ? row.Significance_Total
                          : row.Significance_Answer,
                    });
                    if (row.totalWeightedShare !== null) {
                      allMetricsNull = false;
                    }
                  });
                });

                if (allMetricsNull) {
                  item.metric = [];
                  item.noData = true;
                  item.noDataAvailble = true;
                } else {
                  item.noDataAvailble = false;
                }
              }
            }

            itemLev1.metric_value = metricsAvailable;
          });
          if (
            item.noDataAvailble != null &&
            item.noDataAvailble != undefined &&
            item.noDataAvailble == false
          ) {
            item.widgetNotConfigured = item.metric.length > 0 ? false : true;
          } else {
            if (
              item.noDataAvailble == null ||
              item.noDataAvailble == undefined
            ) {
              item.widgetNotConfigured = item.metric.length > 0 ? false : true;
            } else {
              item.widgetNotConfigured = false;
            }
          }
        });
      }
      arrWithoutTrend.push(comp.itemWithoutTrendItems);
    }
    comp._configService.allPageOutputData = arr;
    comp._configService.allPageOutputDataWithoutTrend = arrWithoutTrend;
    comp.output_details = {
      pageData: comp.pageData,
      widgetData: comp.itemWithoutTrendItems,
    };

    comp._configService.allPageOutputData = arr;
    comp._configService.allPageOutputDataWithoutTrend = arrWithoutTrend;
    comp._configService.itemWithTrendData = arr;
    comp.selectedWidgetName = 'Awareness';
    comp.selectedWidgetID = comp.promptData.Widget_ID;


    comp.filteredWidgetData = comp.output_details.widgetData.filter(
      (widget: any) => widget.previewChartPostion == comp.selectedWidgetID
    )[0];
    comp.filteredWidgetID = comp.filteredWidgetData.previewChartPostion;



    const widgetName = comp.filteredWidgetData?.widget_name || 'Widget';
    const compareAcross = comp.promptData?.Snapshot_Type || SNAPSHOT_TYPES.SINGLE_BRAND;
    const metrics = comp.filteredWidgetData?.metric?.[0]?.metric_value || [];
    const pData = comp.promptData || {};
    const marketsData = pData.Markets || pData.markets || pData.Market || pData.market || [];
    const timesData = pData.Time_Period || pData.time_period || pData.TimePeriod || pData.timeperiod || [];
    const filtersData = pData.Filter_Audience || pData.filter_audience || pData.Filter || pData.filter || [];

    const extractNameValue = (item: any) => {
      if (typeof item === 'string') {
        const parts = item.split('|');
        if (parts.length >= 2) {
          return parts[parts.length - 2] + ': ' + parts[parts.length - 1];
        }
        return parts.pop();
      }
      if (item && item.name) return item.name;
      if (item && item.Child) return item.Child;
      if (item && item.option) return item.option;
      return String(item);
    };

    const metricNames = metrics.map((m: any) => m.option || m.metric_option || '').filter((name: string) => name).join(', ');
    const baseType = comp.promptData?.isTotalBase ? BASE_TYPES.TOTAL : BASE_TYPES.ANSWER;

    const extractLastPart = (item: any) => {
      if (typeof item === 'string') return item.split('|').pop();
      if (item && item.name) return item.name;
      if (item && item.Child) return item.Child;
      if (item && item.option) return item.option;
      return String(item);
    };

    const marketNames = marketsData.length > 0 ? marketsData.map((m: any) => extractLastPart(m)).join(', ') : 'All Markets';
    const timePeriodNames = timesData.length > 0 ? timesData.map((t: any) => extractNameValue(t)).join(', ') : 'All Time Periods';
    const filterNames = filtersData.length > 0 ? filtersData.map((f: any) => extractNameValue(f)).join(', ') : 'All Respondents';
    const brandsData = pData.Primary_Brands || pData.primary_brands || pData.Brands || pData.brands || [];
    const brandsNames = brandsData.length > 0 ? brandsData.map((b: any) => extractNameValue(b)).join(', ') : 'All Brands';

    let selections = 'Time Period: ' + timePeriodNames + '; Brands: ' + brandsNames;
    if (filtersData.length > 0) {
      selections += '; Filters: ' + filterNames;
    }

    comp._configService.selectionSummaryTextTitle =
      'Compare Across: ' + compareAcross + ': ' + widgetName +
      ' || Entry Point: Markets: ' + marketNames +
      ' || Selections: ' + selections +
      ' || Significance: None' +
      ' || Base: ' + baseType +
      ' || Weight: Primary Weight';


    if (!comp.Snapshotdataforexport) {
      comp.Snapshotdataforexport = comp._configService.getDropdownValues();
    }

    comp.Snapshotdataforexport.compareAcross = compareAcross;
    comp.Snapshotdataforexport.snapshotName = comp.promptData?.Snapshot_Name || 'Snapshot';
    comp.Snapshotdataforexport.clientName = comp.selected_client;
    comp.Snapshotdataforexport.studyName = comp.selected_study;
    comp.Snapshotdataforexport.selectionSummary = comp._configService.selectionSummaryTextTitle;
    comp.Snapshotdataforexport.baseType = baseType;

    setTimeout(() => comp.showSnapshot = true, 5);
    setTimeout(() => MarkdownPayloadHelper.buildSnapshotMarkdown(comp), 500);
  }
}
